import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { useMatchedPatients } from '@ctw/shared/api/fhir/patient-helper';
import {
  SYSTEM_SSN,
  SYSTEM_SSN_OID,
  SYSTEM_ZUS_UNIVERSAL_ID,
} from '@ctw/shared/api/fhir/system-urls';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import {
  DefinitionList,
  type DefinitionListItem,
  hasNonEmptyDefinitionListItems,
} from '@ctw/shared/components/definition-list';
import { canonicalizeContactInfo } from '@ctw/shared/content/demographics/helpers/canonicalization';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { ehrDisplayNames, ehrSystemURLs } from '@ctw/shared/context/ehr-system-urls';
import type { UseResourceDrawerOpenDrawerArgs } from '@ctw/shared/context/resource-drawer-context';
import { formatPhoneNumber } from '@ctw/shared/utils/phone-number';
import { tw } from '@ctw/shared/utils/tailwind';
import { capitalize, compact } from 'lodash-es';
import { type ReactNode, useMemo, useState } from 'react';

const KNOWN_EHR_SYSTEMS: Array<{ system: string; label: string }> = Object.entries(
  ehrSystemURLs,
).flatMap(([name, systems]) =>
  systems.map((system) => ({
    system,
    label: ehrDisplayNames[name as keyof typeof ehrDisplayNames] ?? capitalize(name),
  })),
);

const EHR_SYSTEM_LABELS = new Map(KNOWN_EHR_SYSTEMS.map((ehr) => [ehr.system, ehr.label]));

// NEVER show SSN identifiers
const EXCLUDED_IDENTIFIER_SYSTEMS = new Set([SYSTEM_ZUS_UNIVERSAL_ID, SYSTEM_SSN, SYSTEM_SSN_OID]);

export const patientDrawerAction: UseResourceDrawerOpenDrawerArgs<'Patient'>['action'] = ({
  model,
}) => ({
  label: 'Patient',
  title: model.displayFull || model.display,
  subtitle: buildSubtitle(model),
  badges: compact([
    model.active === false && { variant: 'caution' as const, content: 'Inactive' },
    model.isTestPatient && { variant: 'meta' as const, content: 'Test Patient' },
  ]),
  content: {
    body: <PatientDrawerBody model={model} />,
  },
});

function PatientDrawerBody({ model }: { model: PatientModel }) {
  const demographicsItems = buildDemographicsItems(model);

  return (
    <div className={tw`flex flex-col gap-5`}>
      <ContentCard variant="focal" title="Identity">
        <IdentityCardContent model={model} />
      </ContentCard>

      <ContactCard model={model} />

      {hasNonEmptyDefinitionListItems(demographicsItems) && (
        <ContentCard variant="focal" title="Demographics">
          <DefinitionList items={demographicsItems} />
        </ContentCard>
      )}
    </div>
  );
}

function buildSubtitle(model: PatientModel): string {
  const segments = compact([
    model.gender ? capitalize(model.gender) : undefined,
    model.dob ? `${model.dob}${model.age !== undefined ? ` (age ${model.age})` : ''}` : undefined,
  ]);
  return segments.join(' • ');
}

function IdentityCardContent({ model }: { model: PatientModel }) {
  const [expanded, setExpanded] = useState(false);
  const identifiers = model.resource.identifier ?? [];
  const ehrMatch = findEhrIdentifier(model);

  const excludedSystems = new Set(EXCLUDED_IDENTIFIER_SYSTEMS);
  if (ehrMatch) {
    excludedSystems.add(ehrMatch.system);
  }
  const otherIdentifiers = identifiers.filter(
    (id) => id.value && !excludedSystems.has(id.system ?? ''),
  );

  const items: Array<DefinitionListItem> = compact([
    { label: 'UPID', value: model.UPID ?? '' },
    ehrMatch && { label: ehrMatch.label, value: ehrMatch.value },
    otherIdentifiers.length > 0 && {
      label: 'Other identifiers',
      value: expanded ? (
        <div className={tw`flex flex-col gap-1`}>
          {otherIdentifiers.map((id) => (
            <div key={`${id.system ?? ''}:${id.value ?? ''}`} className={tw`text-base`}>
              <span className={tw`text-content-subtle`}>{formatIdentifierSystem(id.system)}</span>
              {' — '}
              {id.value}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className={tw`cursor-pointer self-start text-primary-text hover:text-primary-main`}
          >
            Show less
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={tw`cursor-pointer text-primary-text hover:text-primary-main`}
        >
          {otherIdentifiers.length} more
        </button>
      ),
    },
  ]);

  return <DefinitionList items={items} />;
}

export function formatIdentifierSystem(system: string | undefined): string {
  if (!system) {
    return 'Identifier';
  }
  const ehrLabel = EHR_SYSTEM_LABELS.get(system);
  if (ehrLabel) {
    return ehrLabel;
  }
  const oidMatch = system.match(/^urn:oid:(.+)/);
  if (oidMatch) {
    return `OID ${oidMatch[1]}`;
  }
  try {
    const url = new URL(system);
    const lastSegment = url.pathname.split('/').filter(Boolean).pop();
    return lastSegment ?? url.hostname;
  } catch {
    return system;
  }
}

function findEhrIdentifier(
  model: PatientModel,
): { system: string; label: string; value: string } | undefined {
  const identifiers = model.resource.identifier ?? [];
  for (const ehr of KNOWN_EHR_SYSTEMS) {
    const match = identifiers.find((id) => id.system === ehr.system);
    if (match?.value) {
      return { system: ehr.system, label: ehr.label, value: match.value };
    }
  }
  return undefined;
}

function ContactCard({ model }: { model: PatientModel }) {
  const { requestContext } = useCTW();
  const matchedPatients = useMatchedPatients({ includeCurrentBuilder: true });
  const patients = matchedPatients.data ?? [model];
  const canonical = useMemo(
    () => canonicalizeContactInfo(requestContext.builderId, patients),
    [requestContext.builderId, patients],
  );

  const phoneItems: Array<DefinitionListItem> = dedupeLabels(
    canonical.phones.map((phone) => {
      const use = phone.uses.find((u) => u !== 'unknown');
      return {
        label: use ? `Phone (${capitalize(use)})` : 'Phone',
        value: formatPhoneNumber(phone.value),
      };
    }),
  );

  const emailValue = canonical.emails[0]?.value?.toLowerCase() ?? '';
  const addressValue = canonical.addresses[0]?.value ?? '';

  const items: Array<DefinitionListItem> = compact([
    ...phoneItems,
    { label: 'Email', value: emailValue },
    { label: 'Address', value: addressValue },
  ]);

  return (
    <ContentCard variant="focal" title="Contact">
      <DefinitionList items={items} />
    </ContentCard>
  );
}

export function dedupeLabels(
  items: Array<{ label: string; value: string | ReactNode }>,
): Array<DefinitionListItem> {
  // If there are duplicate labels, append a count to make them unique (e.g. "Phone", "Phone 2", etc.)
  const counts = new Map<string, number>();
  return items.map((item) => {
    const count = (counts.get(item.label) ?? 0) + 1;
    counts.set(item.label, count);
    return {
      label: count > 1 ? `${item.label} ${count}` : item.label,
      value: item.value,
    };
  });
}

function buildDemographicsItems(model: PatientModel): Array<DefinitionListItem> {
  return [
    { label: 'Marital status', value: model.maritalStatus ?? '' },
    { label: 'Managing organization', value: model.organizationName ?? '' },
  ];
}
